import { useCallback, useRef } from 'react';

import { UPLOADING_STATUS } from 'src/data/common';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import {
  getResourceContentErrorMessage,
  graphQLErrorCodeToResourceUploadErrorCode,
  RESOURCE_CONTENT_TYPE,
  RESOURCE_STATUS,
} from 'src/data/performance';
import { PROMPT_ACTION } from 'src/data/prompt';
import { openResourceInNewTab } from 'src/lib/browser';
import { UploadError } from 'src/lib/errors';
import * as events from 'src/lib/events';
import { getParsedGraphQLFirstError } from 'src/lib/graphql';
import { useBrandBriefsMenuItems } from 'src/services/useBrandBriefsMenuItems';
import { useIsApplicationModeInfinity } from 'src/services/useIsApplicationModeInfinity';
import { useTrackingEvent } from 'src/services/useTrackingEvent';
import { useUploadResourceFile } from 'src/services/useUploadResourceFile';
import { useBrandVoicesActions, useBrandVoicesAssets } from 'src/store/brandVoices';
import { useCustomerActions, useCustomerAudioVideoResourcesEnabled } from 'src/store/customer';
import { useNotificationActions } from 'src/store/notification';
import { useBrandBriefPopup } from 'src/views/BrandBriefPopup';
import { useUpgradePopup } from 'src/views/UpgradePopup';

import { useAvailableResources } from './useAvailableResources';
import { useExternalChannelMenuProps } from './useExternalChannelMenuProps';

export const usePreparePromptInstructionsProps = ({
  eventPage,
  generationTool,
  provideUrlResources = false,
  provideFileResources = false,
  onAction,
  onUploadProgress,
  displayEditBrief = false,
}) => {
  const isApplicationModeInfinity = useIsApplicationModeInfinity();
  const externalChannelMenuProps = useExternalChannelMenuProps();
  const customerAudioVideoResourcesEnabled = useCustomerAudioVideoResourcesEnabled();

  const { brandBriefs, loadingBrandVoiceAssets, brandVoicesAssets } = useBrandVoicesAssets();

  const filteredResources = useAvailableResources({
    provideUrlResources,
    provideFileResources,
  });

  const notifications = useNotificationActions();
  const customerActions = useCustomerActions();
  const brandVoicesActions = useBrandVoicesActions();
  const openUpgradePopup = useUpgradePopup();
  const openBrandBriefPopup = useBrandBriefPopup();
  const trackEvent = useTrackingEvent();
  const uploadResourceFile = useUploadResourceFile();

  const brandBriefsMenuItems = useBrandBriefsMenuItems({
    brandVoicesAssets,
    isLoading: loadingBrandVoiceAssets,
    displayEditBrief,
  });

  /**
    "uploadResourceFile" action handler
   */
  const handleUploadResourceFileActionRef = useRef();
  handleUploadResourceFileActionRef.current = async ({ resourceType, file, uploadId, index }) => {
    // Reflect the "start" of the upload process
    onUploadProgress?.({
      status: UPLOADING_STATUS.started,
      resource: { contentType: resourceType, file },
      uploadId,
      index,
    });

    try {
      const resource = await uploadResourceFile({ resourceType, file });

      if (resource.status === RESOURCE_STATUS.active) {
        // Notify listeners about the successful upload
        onUploadProgress?.({
          status: UPLOADING_STATUS.success,
          resource,
          uploadId,
          index,
        });
      }

      return resource;
    } catch (error) {
      // Notify listeners about the upload error
      const preparedError = prepareError({ resourceType, error });
      onUploadProgress?.({
        status: UPLOADING_STATUS.error,
        error: preparedError,
        uploadId,
        index,
      });
    }
  };

  const handleAction = useCallback(
    (action, data = null) => {
      switch (action) {
        case PROMPT_ACTION.addBrandBrief: {
          if (customerActions.featureLimitExceeded(FEATURE_LIMITS.brandBriefPerCustomer)) {
            openUpgradePopup(FEATURE_LIMITS.brandBriefPerCustomer);
            return;
          }

          const brandVoice = data?.brandVoice || brandVoicesActions.getDefaultBrandVoice();
          return openBrandBriefPopup({ brandVoice });
        }

        case PROMPT_ACTION.insertBrandBrief: {
          trackEvent(events.PROMPTS.promptMessageBankInserted, {
            page: eventPage,
            generationTool,
            widget: data?.widget,
          });
          break;
        }

        case PROMPT_ACTION.insertUrl: {
          trackEvent(events.PROMPTS.promptResourceInserted, {
            page: eventPage,
            generationTool,
            type: RESOURCE_CONTENT_TYPE.url,
            widget: data?.widget,
          });
          break;
        }

        case PROMPT_ACTION.insertFileResource: {
          trackEvent(events.PROMPTS.promptResourceInserted, {
            page: eventPage,
            generationTool,
            type: data?.resource?.contentType,
            widget: data?.widget,
          });
          break;
        }

        case PROMPT_ACTION.displayMessage: {
          notifications.displayMessage(data.message);
          break;
        }

        case PROMPT_ACTION.trackEvent: {
          const { event, ...attributes } = data;
          trackEvent(event, {
            page: eventPage,
            generationTool,
            ...attributes,
          });
          break;
        }

        case PROMPT_ACTION.uploadResourceFile: {
          return handleUploadResourceFileActionRef.current(data);
        }

        case PROMPT_ACTION.errorUploadingFile: {
          notifications.displayError(data);
          break;
        }

        case PROMPT_ACTION.openResourceInNewTab: {
          openResourceInNewTab({
            url: data.resource.file.originalUrl,
            isImage: data.resource.contentType === RESOURCE_CONTENT_TYPE.image,
          });
          break;
        }

        default:
          break;
      }

      // Next, pass action to parent component
      return onAction?.(action, data);
    },
    [
      onAction,
      customerActions,
      brandVoicesActions,
      openBrandBriefPopup,
      openUpgradePopup,
      trackEvent,
      eventPage,
      generationTool,
      notifications,
    ]
  );

  return {
    isApplicationModeInfinity,
    externalChannelMenuProps,
    brandBriefs,
    availableResources: filteredResources,
    shouldIncludeAudioVideoResources: customerAudioVideoResourcesEnabled,
    onAction: handleAction,
    brandBriefsMenuItems,
  };
};

const prepareError = ({ resourceType, error }) => {
  if (error?.graphQLErrors) {
    const parsedError = getParsedGraphQLFirstError(error);
    if (parsedError) {
      const uploadErrorCode = graphQLErrorCodeToResourceUploadErrorCode(parsedError.code);
      if (uploadErrorCode) {
        const message = getResourceContentErrorMessage(resourceType, uploadErrorCode);
        return new UploadError(message, uploadErrorCode);
      }
    }
  }

  return error;
};
